import { ArrowBack, ArrowForward, CancelOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import DateRange from "../../../../components/filter/DateRange";
import SearchBar from "../../../../components/filter/SearchBar";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import RowStack from "../../../../components/wrappers/RowStack";
import { AppointmentFilteringContext } from "../../../../contexts/AppointmentFiltering";
import * as F from "../../utils/Functions";
import { appointmentParamKeys } from "./DesktopReservations";
import AppointmentMapper from "../../../appointments/components/tabs/components/AppointmentMapper";
import useUser from "../../../../hooks/useUser";
import RowsPerPageMobileSelect from "../../../../components/filter/RowsPerPageMobileSelect";
import { FilterAlt } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import fabric from "../../../../assets/png/darkBackground.png"
import { ThemeContext, useThemeContext } from "../../../../contexts/ThemeContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export default function MobileReservations() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data,
    isLoading,
    handleWildCardData,
    handleStatusFiltering,
    resetFilter,
    size,
  } = React.useContext(AppointmentFilteringContext);
  const { preferance } = useUser();

  const [previousPagesCount, setPreviousPagesCount] = React.useState(1);
  const { mode } = React.useContext(ThemeContext);
  const isDark = mode == "dark";
  const [open, setOpen] = React.useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  function isStatusChecked(status) {
    const statusParams = searchParams.get(appointmentParamKeys.status);
    const prev = statusParams ? statusParams.split("+") : [];
    return prev.includes(String(status));
  }

  function getRows() {
    if (searchParams.get(appointmentParamKeys.tableRow) == null) {
      return preferance.appointmentTableRowsPerPage ?? 10;
    }
    return searchParams.get(appointmentParamKeys.tableRow) ?? 10;
  }
  const loading = !data || isLoading;

  function getPagesCount() {
    return data ? Math.ceil(data.pages / size) || 1 : 1;
  }
  React.useEffect(() => {
    if (data && !isLoading) {
      setPreviousPagesCount(getPagesCount());
    }
  }, [data, isLoading, size]);

  function getCurrentPage() {
    return Math.max(
      0,
      Number(searchParams.get(appointmentParamKeys.tablePage)) || 0
    );
  }

  function handleChangePage(newPage) {
    if (newPage < 0 || newPage >= getPagesCount()) return;
    searchParams.set(appointmentParamKeys.tablePage, newPage.toString());
    setSearchParams(searchParams);
  }

  const handleChangeRowsPerPage = (e) => {
    searchParams.set(appointmentParamKeys.tableRow, Number(e.target.value));
    searchParams.set(appointmentParamKeys.tablePage, 0);
    setSearchParams(searchParams);
  };

  return (
    <Fragment>
      <Box
        sx={{
          position: "sticky",
          top: 50,
          left: 0,
          right: 0,
          zIndex: 99,
          width: "100%",
          backgroundColor: "background.default",
          backgroundImage: isDark ? `url(${fabric})` : null,
          pt: 3,
          pb: 4,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: "85.3%",
            mx: "auto",
            p: 1,
            mt: 2,
          }}
        >
          <RowStack>
            <SearchBar
              lsVal={appointmentParamKeys.search}
              getData={(value) => handleWildCardData(value)}
            />
            <IconButton aria-label={t("Filter")} title={t("Filter")} onClick={handleClickOpen}>
              <FilterAlt />
            </IconButton>
          </RowStack>
        </Paper>
      </Box>
      <PageContentWrapper marginTop={8}>
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen={true}
          TransitionComponent={Transition}
          TransitionProps={{
            mountOnEnter: true,
            unmountOnExit: true,
            timeout: 300,
          }}
        >
          {/* Filter title */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              my: 1,
              position: "relative",
            }}
          >
            <IconButton
              aria-label={t("Close")}
              title={t("Close")}
              onClick={handleClose}
              size="large"
              sx={{ color: "red" }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5">{t("Filter")}</Typography>
          </Stack>
          <Container sx={{ mt: 1 }}>
            {/* Status group */}
            <Box>
              <Typography variant="h6">{t("Status")}</Typography>
              <Divider sx={{ mb: 1 }} />
              <FormControl component="fieldset">
                <FormGroup
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 2.5,
                    ml: 2,
                  }}
                >
                  {F.statusTitle.map((status) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isStatusChecked(status.value)}
                          onChange={() => handleStatusFiltering(status.value)}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center">
                          <Box sx={{ mr: 1, mt: 0.5 }}>
                            {F.getIconByStatus(status.value)}
                          </Box>
                          {t(status.title)}
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>
            {/* Date group */}
            <Box sx={{ mb: 2.5 }}>
              <Box>
                <Typography variant="h6">{t("Date")}</Typography>
                <Divider />
              </Box>
              <RowStack>
                <DateRange
                  paramsEnd={appointmentParamKeys.endDate}
                  paramsStart={appointmentParamKeys.startDate}
                />
              </RowStack>
            </Box>
            {/* Other group */}
            <Box>
              <Typography variant="h6">{t("Other")}</Typography>
              <Divider sx={{ mb: 1 }} />
            </Box>
            <RowStack>
              <RowsPerPageMobileSelect
                getRows={getRows}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </RowStack>
            <Divider sx={{ mt: 1 }} />
            <RowStack justifyContent="space-evenly">
              <Button
                startIcon={<CancelOutlined />}
                color="error"
                onClick={() => resetFilter()}
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  m: 2,
                }}
              >
                {t("Reset")}
              </Button>
            </RowStack>
          </Container>
        </Dialog>

        {loading ? (
          <Stack justifyContent={"center"} alignItems={"center"}>
            <CircularProgress />
          </Stack>
        ) : (
          <Fragment>
            <AppointmentMapper
              appointments={data["appointments"]}
              title="NoUpcomingAppointments"
            />
          </Fragment>
        )}
      </PageContentWrapper>
      {/* Sticky pager */}
      {loading ? null : (
        <Box
          component="footer"
          sx={{
            position: "sticky",
            bottom: -1.5,
            left: 0,
            right: 0,
            width: "100vw",
            zIndex: 99,
            backgroundColor: "background.default",
            backgroundImage: isDark ? `url(${fabric})` : "none",
            py: 1.25,
          }}
        >
          <Box
            component="nav"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <IconButton
              color="primary"
              onClick={() => handleChangePage(getCurrentPage() - 1)}
              disabled={getCurrentPage() === 0}
              title={t("PreviousPage")}
            >
              <ArrowBack />
            </IconButton>
            {getCurrentPage() + 1} /{" "}
            {loading ? previousPagesCount : getPagesCount()}
            <IconButton
              color="primary"
              onClick={() => handleChangePage(getCurrentPage() + 1)}
              disabled={getCurrentPage() + 1 >= getPagesCount()}
              title={t("NextPage")}
            >
              <ArrowForward />
            </IconButton>
          </Box>
        </Box>
      )}
    </Fragment>
  );
}
